import { track } from "@amplitude/analytics-react-native";
import { useCallback, useState } from "react";
import { Platform } from "react-native";
import { amp } from "src/api/amplitude";
import queueDownload from "src/api/asset-persist/queueDownload";
import { RemoteDownloadAssetInfo } from "src/api/asset-persist/types";
import PhotoVariant from "src/api/graphql/__generated__/enums/PhotoVariant";
import isClip from "src/constants/isClip";
import haptic from "src/helpers/haptic";
import log from "src/helpers/log";
import useDevice from "src/hooks/useDevice";
import { useModal } from "src/modals";
import GlobalModal from "src/modals/configs/GlobalModal";
import toast from "src/swsh-native/toast";
import { ShareContextType, useShareContext } from "../../contexts/ShareContext";
import CommonPrefetchedData from "../../helpers/PrefetchedData/CommonPrefetchedData";
import { DownloadLivePhotosPayload } from "../../helpers/downloadLivePhotos";
import getUniqueTypes from "../../helpers/getUniqueTypes";
import shouldShowSaveTutorial from "../../helpers/shouldShowSaveTutorial";
import { ShareDownloadDataType } from "./shareDownloadHelpers";

const useShareDownload = (context?: ShareContextType) => {
	const { prefetched, onComplete } = useShareContext(context);
	const { native } = useDevice();
	const [loading, setLoading] = useState(false);
	const { presentModal } = useModal<GlobalModal>();

	const handlePress = useCallback(
		async (data: ShareDownloadDataType) => {
			try {
				setLoading(true);
				track(amp.share.download, {
					types: getUniqueTypes(data),
					dataLength: data.length,
				});
				const imagePrefetchedData: CommonPrefetchedData[] = [];
				const videoPrefetchedData: CommonPrefetchedData[] = [];
				const livePhotosPrefetchedData: DownloadLivePhotosPayload = [];

				haptic("Light");
				for (const item of data) {
					if (item.successAmplitude) {
						track(item.successAmplitude.eventName, {
							...item.successAmplitude.eventProperties,
							shareType: item.type,
							shareAction: "Download",
						});
					}
					if (item.type === "Image") {
						const imageUrlData = prefetched.current[item.url];
						if (!imageUrlData) {
							log.error({ url: item.url }, "Prefetched image data not found");
							continue;
						}
						imagePrefetchedData.push(imageUrlData);
					} else if (item.type === "Video") {
						const videoUrlData = prefetched.current[item.videoUrl];
						if (!videoUrlData) {
							log.error({ url: item.videoUrl }, "Prefetched video data not found");
							continue;
						}
						videoPrefetchedData.push(videoUrlData);
					} else if (item.type === "LivePhoto") {
						const imageUrlData = prefetched.current[item.url];
						const videoUrlData = prefetched.current[item.videoUrl];
						if (!imageUrlData) {
							log.error({ url: item.url }, "Prefetched image data not found");
							continue;
						}
						if (!videoUrlData || Platform.OS !== "ios" || isClip) {
							imagePrefetchedData.push(imageUrlData);
							continue;
						}
						livePhotosPrefetchedData.push({
							imageUrlData,
							videoUrlData,
						});
					}
				}

				const photosQueue: RemoteDownloadAssetInfo[] = [];
				if (livePhotosPrefetchedData.length > 0) {
					photosQueue.push(
						...livePhotosPrefetchedData.map(
							(data) =>
								({
									variant: PhotoVariant.LivePhoto,
									image: {
										url: data.imageUrlData.url,
										uri: data.imageUrlData.uri,
										metadata: data.imageUrlData.metadata,
									},
									video: {
										url: data.videoUrlData.url,
										uri: data.videoUrlData.uri,
										metadata: data.videoUrlData.metadata,
									},
								}) satisfies RemoteDownloadAssetInfo,
						),
					);
				}

				if (!native && shouldShowSaveTutorial) {
					const hasImage = data.some(
						(item) => item.type === "Image" || item.type === "LivePhoto",
					);
					const hasVideo = data.some((item) => item.type === "Video");
					presentModal("SaveTutorialModal", {
						props: {
							data: [...imagePrefetchedData, ...videoPrefetchedData],
							saveVariant:
								hasImage && hasVideo
									? "imageAndVideo"
									: hasImage
										? "image"
										: "video",
						},
						closeOnHookUnmount: false,
					});
				} else {
					photosQueue.push(
						...imagePrefetchedData.map(
							(data) =>
								({
									variant: PhotoVariant.Image,
									image: {
										url: data.url,
										uri: data.uri,
										metadata: data.metadata,
									},
								}) satisfies RemoteDownloadAssetInfo,
						),
						...videoPrefetchedData.map(
							(data) =>
								({
									variant: PhotoVariant.Video,
									video: {
										url: data.url,
										uri: data.uri,
										metadata: data.metadata,
									},
								}) satisfies RemoteDownloadAssetInfo,
						),
					);
				}
				await queueDownload(photosQueue);
			} catch (err) {
				log.error({ err }, "Error downloading data");
				toast({
					preset: "error",
					title: "Something went wrong",
					message: "Error downloading data",
				});
			} finally {
				setLoading(false);
				onComplete?.();
			}
		},
		[native, onComplete, prefetched, presentModal],
	);

	return {
		handlePress,
		loading,
	};
};

export default useShareDownload;
